import React, { useState, useEffect } from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'

const STORAGE_KEY = 'hidden-prop-ev-bets'

// ─── Key: no odds so bet stays hidden even if odds change on refresh ──────────
export const oppKey = (opp) =>
  `${opp.selection}|${opp.market_type}`

// A whole match hidden at once, from the track modal's "Also hide the rest of
// this match". Start time is in the key so the same two teams meeting again
// later are not caught by it.
export const matchKey = (opp) =>
  `match|${opp.match}|${opp.commence_time}`

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  })

const parseSelection = (sel) => {
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}


// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHiddenBets() {
  const [hiddenBets, setHiddenBets] = useState({})
  const [showModal,  setShowModal]  = useState(false)

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([key, data]) => {
      const cutoff = new Date(new Date(data.commence_time + 'Z').getTime() + 60 * 60 * 1000)
      if (now < cutoff) cleaned[key] = data
    })
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  const hideOpp = (opp, { wholeMatch = false } = {}) => {
    if (wholeMatch) {
      const updated = {
        ...hiddenBets,
        [matchKey(opp)]: {
          scope:         'match',
          sport:         opp.sport,
          match:         opp.match,
          commence_time: opp.commence_time,
        },
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      setHiddenBets(updated)
      return
    }
    const key = oppKey(opp)
    const updated = {
      ...hiddenBets,
      [key]: {
        selection:    opp.selection,
        match:        opp.match,
        market_type:  opp.market_type,
        commence_time:opp.commence_time,
        ev_percentage:opp.ev_percentage,
        original_odds:opp.original_odds,
        bookmakers:   opp.bookmakers || [],
      },
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpp = (key) => {
    const updated = { ...hiddenBets }
    delete updated[key]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAll = () => {
    localStorage.removeItem(STORAGE_KEY)
    setHiddenBets({})
    setShowModal(false)
  }

  const isHidden = (opp) => !!(hiddenBets[oppKey(opp)] || hiddenBets[matchKey(opp)])

  return { hiddenBets, hideOpp, unhideOpp, clearAll, isHidden, showModal, setShowModal }
}

// A hidden match has no selection, odds or bookmaker, so it gets its own row:
// the match where a bet's name would be, and "Whole match" in place of the line.
function HiddenMatchRow({ bet, onUnhide, mobile }) {
  return (
    <div className="phm-row">
      <div className="phm-row-inner">
        {mobile ? (
          <div className="bcm-card-name-block">
            <div className="bcm-player-name">{bet.match}</div>
            <div className="bcm-match-name">Whole match</div>
            <div className="bcm-match-time">{formatDateTime(bet.commence_time)}</div>
          </div>
        ) : (
          <div className="phm-row-info">
            <div className="phm-row-sel">
              <span className="phm-player">{bet.match}</span>
            </div>
            <div className="phm-sub">Whole match</div>
            <div className="phm-sub">{formatDateTime(bet.commence_time)}</div>
          </div>
        )}
        <div className="phm-row-actions">
          <button className="phm-unhide-btn" onClick={onUnhide} data-tooltip="Unhide">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function HiddenBetsModal({ isOpen, hiddenBets, onUnhide, onUnhideAll, onClose, mobile = false }) {
  if (!isOpen) return null

  const entries = Object.entries(hiddenBets)

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="phm-modal">

        <div className="phm-header">
          <div className="phm-header-left">
            <span className="phm-title">Hidden Bets</span>
            {entries.length > 0 && <span className="phm-count">{entries.length}</span>}
          </div>
          <div className="phm-header-right">
            {entries.length > 0 && (
              <button className="bc-btn phm-unhide-all" onClick={onUnhideAll}>
                Unhide All
              </button>
            )}
            <button className="bc-panel-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="phm-body">
          {entries.length === 0 ? (
            <div className="phm-empty">No hidden bets</div>
          ) : (
            entries.map(([key, bet]) => {
              if (bet.scope === 'match') {
                return <HiddenMatchRow key={key} bet={bet} onUnhide={() => onUnhide(key)} mobile={mobile} />
              }
              const { name, dir, line } = parseSelection(bet.selection)
              const evClass = bet.ev_percentage >= 10 ? 'bc-ev-high' : bet.ev_percentage >= 5 ? 'bc-ev-mid' : 'bc-ev-low'
              return (
                <div key={key} className="phm-row">
                  <div className="phm-row-inner">

                    {mobile ? (
                      <>
                        <div className="bcm-avatar-wrap">
                          <img
                            src={getPlayerImageUrl(bet.selection, bet.sport)}
                            alt=""
                            className="bcm-avatar"
                            onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
                            onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
                          />
                        </div>

                        <div className="bcm-card-name-block">
                          <div className="bcm-player-name">{name}</div>
                          {dir && (
                            <div className={`bc-sel-badge ${dir}`}>
                              <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                              <span className="bc-sel-badge-num">{line}</span>
                            </div>
                          )}
                          <div className="bcm-match-name">{bet.match}</div>
                          <div className="bcm-match-time">{formatDateTime(bet.commence_time)}</div>
                        </div>

                        <div className="phm-row-actions">
                          <div className="bc-book-cell phm-book-centered">
                            {(bet.bookmakers || []).map(b =>
                              BOOKMAKER_LOGOS[b.name]
                                ? <img key={b.name} src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-book-logo" title={b.name} />
                                : <span key={b.name} className="phm-book-name">{b.name}</span>
                            )}
                          </div>
                          <button className="phm-unhide-btn" onClick={() => onUnhide(key)} title="Unhide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="phm-avatar-wrap">
                          <img
                            src={getPlayerImageUrl(bet.selection, bet.sport)}
                            alt=""
                            className="phm-avatar"
                            onLoad={e  => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
                            onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
                          />
                        </div>

                        <div className="phm-row-info">
                          <span className="phm-player">{name}</span>
                          {dir && (
                            <span className={`bc-sel-badge ${dir}`}>
                              <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                              <span className="bc-sel-badge-num">{line}</span>
                            </span>
                          )}
                          <div className="phm-sub">{bet.match} · {formatDateTime(bet.commence_time)}</div>
                        </div>

                        <div className="phm-row-actions">
                          <div className="bc-book-cell">
                            {(bet.bookmakers || []).map(b =>
                              BOOKMAKER_LOGOS[b.name]
                                ? <img key={b.name} src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-book-logo" title={b.name} />
                                : <span key={b.name} className="phm-book-name">{b.name}</span>
                            )}
                          </div>
                          <span className={`bc-ev-badge ${evClass}`}>
                            +{bet.ev_percentage?.toFixed(2)}%
                          </span>
                          <button className="phm-unhide-btn" onClick={() => onUnhide(key)} title="Unhide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              )
            })
          )}
        </div>

      </div>
    </>
  )
}
